<template>
  <div>
    <div
      class="d-flex justify-content-between align-items-start flex-wrap mb-3"
    >
      <!-- CARD HEADER -->
      <div class="d-flex align-content-center justify-content-between gap-4">
        <h4>{{ sectionHeader }}</h4>
        <HelpCenterButton
          :content-key="sectionContentKey"
          aria-label="Watch list help"
        />
      </div>
      <!-- CARD HEADER -->

      <!-- CALL TO ACTION -->
      <BaseButton
        solid
        rounded
        prepend-icon="ri-add-line"
        @click="onAddWatchList()"
      >
        {{ buttonText }}
      </BaseButton>
      <!-- CALL TO ACTION -->
    </div>

    <!-- SERVER ERROR -->
    <VisitorErrorHandler
      v-if="serverError != null"
      :error="serverError"
      @done="serverError = null"
    />
    <!-- SERVER ERROR -->

    <!-- PAGE SELECTOR -->
    <Perpage
      v-if="!isLoading && !isSchoolSettingsLoading"
      use-first-option-as-default
      use-dynamic-update
      showing-entries
      :options="pageOptions"
      :pagination="paginationWithExtra"
      @on-page-change="setPage"
    />
    <!-- PAGE SELECTOR -->

    <!-- TABLE -->
    <DataTable
      :class="{ 'visitor-loading': isLoading || isSchoolSettingsLoading }"
      :fields="fields"
      :items="list"
    >
      <template #first_name="{ item }">
        <td>
          {{ item.first_name }}
        </td>
      </template>
      <template #last_name="{ item }">
        <td>
          {{ item.last_name }}
        </td>
      </template>
      <template #date_of_birth="{ item }">
        <td>
          {{ formatDate(item.date_of_birth) }}
        </td>
      </template>
      <template #reason="{ item }">
        <td>
          {{ item.reason }}
        </td>
      </template>

      <template #action="{ item }">
        <td class="text-end">
          <Actions
            v-if="!isLoading && !isSchoolSettingsLoading"
            :actions="actions"
            @click-action="handleTableAction($event, item.id)"
          >
          </Actions>
        </td>
      </template>
    </DataTable>
    <!-- TABLE -->

    <!-- PAGINATION -->
    <Pagination
      v-if="pagination.pages > 1 && !isLoading && !isSchoolSettingsLoading"
      :active-page="pagination.activePage"
      :pages="pagination.pages"
      class="cs-pagination mt-4"
      :dots="false"
      :double-arrows="true"
      size="lg"
      align="center"
      @update:active-page="setActivePage"
    ></Pagination>
    <!-- PAGINATION -->
  </div>
</template>

<script>
import HelpCenterButton from "@/v3components/HelpCenterButton.vue"
import VisitorErrorHandler from "@/v3components/VisitorErrorHandler.vue"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import Actions from "@/v3components/shared/DataTable/Actions.vue"
import DataTable from "@/v3components/shared/DataTable/DataTable.vue"
import Pagination from "@/v3components/shared/DataTable/Pagination.vue"
import Perpage from "@/v3components/shared/DataTable/Perpage.vue"
import visitorConstants from "@/constants/visitorConstants"
import passHelpers from "@/helpers/index"
import { computed, defineAsyncComponent, inject, onMounted, ref } from "vue"
import { useStore } from "vuex"

const VisitorCreateWatchlistForm = defineAsyncComponent(
  () =>
    import("@/v3components/Forms/Visitor/ListSettings/VisitorCreateWatchlistForm.vue")
)

export default {
  name: "VisitorWatchListTable",
  components: {
    VisitorErrorHandler,
    BaseButton,
    DataTable,
    Actions,
    Perpage,
    Pagination,
    HelpCenterButton
  },
  props: {
    isSchoolSettingsLoading: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const modal = inject("modal")
    const actionDialog = inject("actionDialog")
    const store = useStore()
    const serverError = ref(null)

    const pageOptions = [
      { label: "25 / Page", value: 25 },
      { label: "50 / Page", value: 50 },
      { label: "100 / Page", value: 100 }
    ]

    const fields = [
      { key: "first_name", label: "First name", sorter: false, filter: false },
      { key: "last_name", label: "Last name", sorter: false, filter: false },
      {
        key: "date_of_birth",
        label: "Date of Birth",
        sorter: false,
        filter: false
      },
      { key: "reason", label: "Reason", sorter: false, filter: false },
      {
        key: "action",
        label: "Action",
        _style: "width: 50px;",
        sorter: false,
        filter: false
      }
    ]

    const actions = [
      {
        label: "Delete",
        icon: "ri-delete-bin-line",
        class: "important",
        action: "delete"
      }
    ]

    const watchlist = computed(() => store.getters["visitorSettings/watchlist"])

    const list = computed(() => watchlist?.value?.data || [])

    const pagination = computed(() => watchlist?.value?.pagination || {})

    const paginationWithExtra = computed(() => {
      return {
        ...pagination.value,
        from:
          Number(pagination?.value?.activePage - 1) *
            Number(pagination?.value?.per_page?.value) +
          1,
        to:
          Number(pagination?.value?.activePage - 1) *
            Number(pagination?.value?.per_page?.value) +
          Number(pagination?.value?.per_page?.value)
      }
    })

    const isLoading = computed(() => watchlist?.value?.isLoading || false)

    const shouldGoBackOnePage = computed(() => {
      const isActivePageGreaterThanOne =
        Number(pagination?.value?.activePage) > 1
      const isTotalPagesGreaterThanOne = Number(pagination?.value?.pages) > 1
      const isLastPage =
        Number(pagination?.value?.activePage) ===
        Number(pagination?.value?.pages)
      const onlyOneItemLeft = Number(list?.value?.length) === 1
      return (
        isActivePageGreaterThanOne &&
        isTotalPagesGreaterThanOne &&
        isLastPage &&
        onlyOneItemLeft
      )
    })

    const setPage = async (option) => {
      if (!option) return

      store.commit("visitorSettings/SET_WATCHLIST_PAGINATION_PER_PAGE", option)
      await store.dispatch("visitorSettings/getWatchlist")
    }

    const setActivePage = async (page) => {
      if (!page) return

      store.commit("visitorSettings/SET_WATCHLIST_PAGINATION_ACTIVE_PAGE", page)
      await store.dispatch("visitorSettings/getWatchlist")
    }

    const handleTableAction = (event, item) => {
      if (event.action == "delete") {
        actionDialog.open(deleteItem, {
          args: item,
          props: { danger: true, title: "Delete", question: "Are you sure?" }
        })
      }
    }

    const deleteItem = async (adultId) => {
      try {
        serverError.value = null
        await store.dispatch("visitorSettings/deleteWatchlist", adultId)
        if (shouldGoBackOnePage?.value) {
          store.commit(
            "visitorSettings/SET_WATCHLIST_PAGINATION_ACTIVE_PAGE",
            Number(pagination?.value?.activePage) - 1
          )
        }
        await store.dispatch("visitorSettings/getWatchlist")
      } catch (error) {
        serverError.value = error
      }
    }

    const onAddWatchList = () => {
      modal.open(VisitorCreateWatchlistForm, {
        size: "sm",
        title: visitorConstants.LIST_SETTINGS.WATCH_LIST_MODAL_TITLE,
        props: {}
      })
    }

    const formatDate = (date) => {
      return passHelpers.getFormattedDateWithoutTimezone(date)
    }

    onMounted(async () => {
      try {
        await store.dispatch("visitorSettings/getWatchlist")
      } catch (error) {
        serverError.value = error
      }
    })

    return {
      isLoading,
      list,
      buttonText: visitorConstants.LIST_SETTINGS.WATCH_LIST_ADD_BUTTON,
      sectionHeader: visitorConstants.LIST_SETTINGS.WATCH_LIST_SECTION_HEADER,
      sectionContentKey: visitorConstants.LIST_SETTINGS.WATCH_LIST_LEARN_MORE,
      serverError,
      pageOptions,
      pagination,
      paginationWithExtra,
      fields,
      actions,
      handleTableAction,
      setPage,
      setActivePage,
      formatDate,
      onAddWatchList
    }
  }
}
</script>

<style scoped></style>
